import type { RouteDetail, RoutePlan, RouteSource, RouteSummary, RouteVisibility } from '@motogo/shared';

import { API_BASE_URL, motogoFetch } from './api';

// The saved-route data shapes (RouteSummary, RouteDetail, RoutePlan) and pure helpers
// (mergeRoutes, slicePlanSegments) live in @motogo/shared; this module keeps only the
// API-coupled fetch functions.

export interface SaveRouteInput {
  /** Client-generated GUID — retried saves are idempotent. */
  id: string;
  name: string;
  description?: string | null;
  source: RouteSource;
  /** Full geometry as [lon, lat] pairs (GeoJSON order). */
  coordinates: [number, number][];
  durationSeconds?: number | null;
  plan?: RoutePlan | null;
}

async function throwResponseError(res: Response, fallback: string): Promise<never> {
  // Backend errors are ProblemDetails ({ title, detail, errors }).
  const problem = (await res.json().catch(() => null)) as {
    title?: string;
    detail?: string;
    errors?: Record<string, string[]>;
  } | null;
  const firstFieldError = problem?.errors ? Object.values(problem.errors)[0]?.[0] : undefined;
  throw new Error(firstFieldError ?? problem?.detail ?? problem?.title ?? `${fallback} (${res.status})`);
}

/**
 * Lists the signed-in user's saved routes, newest first. Returns null on 401 (guest or
 * no session) so callers can fall back to the local mirror rather than treating it as
 * an error.
 */
export async function fetchRoutes(skip = 0, take = 50): Promise<RouteSummary[] | null> {
  const res = await motogoFetch(`${API_BASE_URL}/me/routes?skip=${skip}&take=${take}`);
  if (res.status === 401) return null;
  if (!res.ok) throw new Error(`GET /me/routes failed: ${res.status}`);
  return (await res.json()) as RouteSummary[];
}

/**
 * Fetches one route's full geometry + plan via the shared endpoint — the same call
 * serves owner, follower (M2) and share-link viewer. Returns null on 404 (absent OR not
 * visible — the server never distinguishes) and on 401.
 */
export async function fetchRouteDetail(id: string): Promise<RouteDetail | null> {
  const res = await motogoFetch(`${API_BASE_URL}/routes/${encodeURIComponent(id)}`);
  if (res.status === 401 || res.status === 404) return null;
  if (!res.ok) throw new Error(`GET /routes/${id} failed: ${res.status}`);
  return (await res.json()) as RouteDetail;
}

/**
 * Saves a route. Idempotent on `input.id`: re-sending after an ambiguous failure returns
 * the already-stored copy. Returns null on 401 (not signed in — mirrored locally and
 * retried after sign-in).
 */
export async function saveRoute(input: SaveRouteInput): Promise<RouteDetail | null> {
  const res = await motogoFetch(`${API_BASE_URL}/me/routes`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (res.status === 401) return null;
  if (!res.ok) await throwResponseError(res, 'Saving the route failed');
  return (await res.json()) as RouteDetail;
}

/**
 * Renames a route and/or changes its visibility (wholesale PUT without coordinates keeps
 * geometry/plan untouched). Dropping a followed route to private freezes a snapshot for
 * its followers server-side (unshare).
 */
export async function updateRouteMeta(
  id: string,
  name: string,
  description: string | null,
  visibility?: RouteVisibility,
): Promise<RouteDetail | null> {
  const res = await motogoFetch(`${API_BASE_URL}/me/routes/${encodeURIComponent(id)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name, description, visibility }),
  });
  if (res.status === 401 || res.status === 404) return null;
  if (!res.ok) await throwResponseError(res, 'Updating the route failed');
  return (await res.json()) as RouteDetail;
}

export interface UpdateRouteFullInput {
  name: string;
  description?: string | null;
  /** Replacement geometry as [lon, lat] pairs — fully replaces the stored polyline. */
  coordinates: [number, number][];
  durationSeconds?: number | null;
  /** Replacement recipe; the server requires it alongside coordinates (or absent to clear). */
  plan?: RoutePlan | null;
}

/**
 * Full update: replaces geometry + plan (and name/description) in one PUT — the M3
 * editor's save path for an existing route. Bumps `updatedAt`, which is what propagates
 * the change to followers. Returns null on 401/404; throws on 400 (validation) and 409
 * (frozen snapshot) with the server's ProblemDetails message.
 */
export async function updateRouteFull(id: string, input: UpdateRouteFullInput): Promise<RouteDetail | null> {
  const res = await motogoFetch(`${API_BASE_URL}/me/routes/${encodeURIComponent(id)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (res.status === 401 || res.status === 404) return null;
  if (!res.ok) await throwResponseError(res, 'Updating the route failed');
  return (await res.json()) as RouteDetail;
}

/** Follows a shared route — it appears in the library by reference. Idempotent. */
export async function followRoute(id: string): Promise<boolean> {
  const res = await motogoFetch(`${API_BASE_URL}/routes/${encodeURIComponent(id)}/follow`, {
    method: 'POST',
  });
  if (res.status === 401 || res.status === 404) return false;
  if (!res.ok) await throwResponseError(res, 'Following the route failed');
  return true;
}

/** Removes the follow (no-op when absent). A frozen route losing its last follower is GC'd server-side. */
export async function unfollowRoute(id: string): Promise<void> {
  const res = await motogoFetch(`${API_BASE_URL}/routes/${encodeURIComponent(id)}/follow`, {
    method: 'DELETE',
  });
  if (res.status === 401 || res.status === 404) return;
  if (!res.ok) await throwResponseError(res, 'Unfollowing the route failed');
}

export interface RouteSearchParams {
  /** Search center; omit both for a "newest published" listing. */
  lat?: number;
  lon?: number;
  radiusKm?: number;
  minKm?: number;
  maxKm?: number;
  minMinutes?: number;
  maxMinutes?: number;
  /** Road-character tags (fast/scenic/curvy/straight/gravel) — a route matches on any overlap. */
  character?: string[];
  skip?: number;
  take?: number;
}

/**
 * Discover (M4): public routes near a point, nearest first (newest-published when no
 * location is given). Anonymous-friendly — guests browse.
 */
export async function searchRoutes(params: RouteSearchParams, signal?: AbortSignal): Promise<RouteSummary[]> {
  const query = new URLSearchParams();
  const set = (key: string, value: number | undefined) => {
    if (value !== undefined && Number.isFinite(value)) query.set(key, String(value));
  };
  set('lat', params.lat);
  set('lon', params.lon);
  set('radiusKm', params.radiusKm);
  set('minKm', params.minKm);
  set('maxKm', params.maxKm);
  set('minMinutes', params.minMinutes);
  set('maxMinutes', params.maxMinutes);
  set('skip', params.skip);
  set('take', params.take);
  if (params.character && params.character.length > 0) query.set('character', params.character.join(','));

  const res = await motogoFetch(`${API_BASE_URL}/routes/search?${query.toString()}`, { signal });
  if (!res.ok) await throwResponseError(res, 'Searching routes failed');
  return (await res.json()) as RouteSummary[];
}

/** Deletes a route. Returns false on 401/404 (nothing to delete from this session's view). */
export async function deleteRoute(id: string): Promise<boolean> {
  const res = await motogoFetch(`${API_BASE_URL}/me/routes/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
  if (res.status === 401 || res.status === 404) return false;
  if (!res.ok) throw new Error(`DELETE /me/routes/${id} failed: ${res.status}`);
  return true;
}
